import { useMutation } from "@tanstack/react-query"
import { z } from "zod"
import { getErrorCode } from "@krakentech/blueprint-api"
import { throwKrakenErrorResponse } from "@krakentech/error-intl"
import { ERROR_CODE } from "@/constants/errorCode"
import { PMAX_ERRORS_CODES } from "@/features/meterPowerAnalysis"
import { RunMeterPointPmaxAnalysisMutation } from "@/features/meterPowerAnalysis/graphql/runMeterPointPmaxAnalysis"
import { type InputOf } from "@/graphql"
import { useGraphQLClient } from "@/lib/blueprint-auth/client"
import { logger } from "@/lib/logger"
import { SuggestedPowerAdjustmentSchema } from "@/scalars/oebackend/suggestedPowerAdjustment"
import { type OnboardingFormValues } from "../../wizard"

const RunMeterPointPmaxAnalysisSchema = z
    .object({
        pmaxAnalysis: z.object({
            token: z.string(),
            currentSubscribedMaxPower: z.number(),
            recommendedSubscribedMaxPower: z.number().nullable(),
            suggestedPowerAdjustment: SuggestedPowerAdjustmentSchema,
            yearlyProfitAmountCents: z.number().nullable(),
            prmId: z.string(),
        }),
    })
    .transform((data) => data.pmaxAnalysis)

export function useRunMeterPointPmaxAnalysis() {
    const oeGraphQLClient = useGraphQLClient({ target: "oeBackend" })

    const meterPointPmaxAnalysis = useMutation({
        mutationKey: ["RunMeterPointPmaxAnalysisMutation"],
        mutationFn: async (input: InputOf<typeof RunMeterPointPmaxAnalysisMutation>) =>
            await oeGraphQLClient.request(RunMeterPointPmaxAnalysisMutation, { input }),
    })

    async function handleRunMeterPointPmaxAnalysis(formValues: OnboardingFormValues) {
        const meterPointId = formValues.electricity.supplyPoint.meterPoint.id

        // Early return if the pmax analysis has already been run
        if (meterPointId === formValues.meterPowerAnalysis.meterPointId) return

        try {
            if (!formValues.givenName || !formValues.familyName) {
                throwKrakenErrorResponse({
                    errorCode: ERROR_CODE.INVALID_NAME,
                    message: "Invalid name.",
                })
            }

            const input = {
                lastName: formValues.familyName,
                firstName: formValues.givenName,
                meterPointId,
                industryConsent: true,
                currentOccupierConsent: true,
            }

            const data = await meterPointPmaxAnalysis.mutateAsync(input)

            const pmaxAnalysis = RunMeterPointPmaxAnalysisSchema.parse(
                data.runMeterPointPmaxAnalysis
            )

            const {
                token,
                prmId,
                currentSubscribedMaxPower,
                recommendedSubscribedMaxPower,
                suggestedPowerAdjustment,
                yearlyProfitAmountCents,
            } = pmaxAnalysis

            return {
                pmaxToken: token,
                meterPointId: prmId,
                currentSubscribedMaxPower,
                recommendedSubscribedMaxPower,
                suggestedPowerAdjustment,
                yearlyProfitAmountCents,
                errorCode: null,
            }
        } catch (error) {
            const errorCode = getErrorCode(error) || null
            const meta = {
                feature: "onboarding",
                subFeature: "meterPower",
                source: "useRunMeterPointPmaxAnalysis",
            }

            const isBusinessError = !!errorCode && PMAX_ERRORS_CODES.includes(errorCode)

            if (isBusinessError) {
                logger.info({ error, meta })
            } else {
                logger.error({ error, meta })
            }

            return {
                recommendedSubscribedMaxPower: null,
                pmaxToken: null,
                suggestedPowerAdjustment: null,
                currentSubscribedMaxPower: null,
                yearlyProfitAmountCents: null,
                meterPointId,
                errorCode,
            }
        }
    }

    return [handleRunMeterPointPmaxAnalysis, meterPointPmaxAnalysis] as const
}
